import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  ArrowRight,
  Zap,
  Shield,
  Users,
  BarChart3,
  Calendar,
  Package,
  Smartphone,
  Monitor,
  Clock,
  DollarSign,
  Bell,
  Database,
  Settings,
  FileText
} from 'lucide-react';
import { PLAY_STORE_URL, WEB_APP_URL } from '../config/urls';

const Pricing = () => {
  const features = [
    {
      icon: <Users size={20} />,
      title: "Customer Management",
      description: "Complete customer database and profiles"
    },
    {
      icon: <Calendar size={20} />,
      title: "Booking Management",
      description: "Smart appointment scheduling and room allocation"
    },
    {
      icon: <Package size={20} />,
      title: "Package Management",
      description: "Create and manage spa packages with flexible pricing"
    },
    {
      icon: <BarChart3 size={20} />,
      title: "Analytics Dashboard",
      description: "Real-time business insights and financial reports"
    },
    {
      icon: <Smartphone size={20} />,
      title: "Mobile App",
      description: "Native Android app with offline functionality"
    },
    {
      icon: <Monitor size={20} />,
      title: "Web App",
      description: "Full-featured web dashboard—use from any browser"
    },
    {
      icon: <Shield size={20} />,
      title: "Enterprise Security",
      description: "Data encryption, backups, and 99.9% uptime"
    },
    {
      icon: <Clock size={20} />,
      title: "Flexible Scheduling",
      description: "Manage therapist schedules and availability"
    },
    {
      icon: <DollarSign size={20} />,
      title: "Expense Tracking",
      description: "Monitor business expenses and generate reports"
    },
    {
      icon: <FileText size={20} />,
      title: "Custom Reports",
      description: "Generate various business performance reports"
    },
    {
      icon: <Settings size={20} />,
      title: "Staff Management",
      description: "Manage staff profiles, commissions, and performance"
    },
    {
      icon: <Bell size={20} />,
      title: "Automated Reminders",
      description: "Send automated reminders to clients and staff"
    },
    {
      icon: <Database size={20} />,
      title: "Cloud Data Sync",
      description: "Secure cloud storage with real-time synchronization"
    }
  ];

  const plans = [
    {
      name: "Monthly Plan",
      originalPrice: "₹1,999",
      offerPrice: "₹499",
      period: "per month",
      savings: "75% OFF",
      features: features,
      cta: "Download App",
      description: "Perfect for small spas getting started"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-slate-50 to-slate-200">
        <div className="container">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-large text-gradient mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              One plan includes both mobile and web. Start your free trial and use the app on any device.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="flex justify-center max-w-2xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                className="relative bg-white rounded-2xl shadow-card border-2 border-primary-500 shadow-lg shadow-primary-500/20 transition-all duration-300 hover:shadow-card-hover hover:-translate-y-2 w-full"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="p-8 lg:p-12">
                  {/* Plan Header */}
                  <div className="text-center mb-8">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-6 text-lg">{plan.description}</p>
                    
                    <div className="mb-6">
                      <div className="flex items-center justify-center gap-3 mb-2">
                        <span className="text-5xl font-bold text-gray-900">{plan.offerPrice}</span>
                        <span className="text-xl text-gray-500 line-through">{plan.originalPrice}</span>
                      </div>
                      <p className="text-base text-gray-600 mb-3">{plan.period}</p>
                      <div className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                        <Zap className="w-4 h-4" />
                        {plan.savings}
                      </div>
                    </div>
                  </div>

                  {/* Features List */}
                  <div className="space-y-4 mb-8">
                    {features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        className="flex items-start gap-3"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: featureIndex * 0.05 }}
                        viewport={{ once: true }}
                      >
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-sm">{feature.title}</h4>
                          <p className="text-gray-600 text-xs">{feature.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3">
                    <a
                      href={WEB_APP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-6 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
                    >
                      <Monitor className="w-5 h-5" />
                      Launch Web App
                    </a>
                    <a
                      href={PLAY_STORE_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-4 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
                    >
                      <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                      </svg>
                      <div className="flex flex-col items-start">
                        <span className="text-[10px] leading-tight">GET IT ON</span>
                        <span className="text-lg font-bold leading-tight">Google Play</span>
                      </div>
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="section-padding bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="container">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large text-gradient mb-4">
              Everything You Need to Manage Your Spa
            </h2>
            <p className="text-small text-gray-600 max-w-2xl mx-auto">
              Your plan includes all features on both mobile and web with no hidden costs or limitations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-primary-500 to-secondary-500 text-white">
        <div className="container">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-large mb-6">
              Ready to Transform Your Spa?
            </h2>
            <p className="text-small text-white/90 mb-10">
              Start your free trial today and experience the difference professional spa management software can make.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
              <a 
                href={WEB_APP_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-6 py-3 rounded-xl hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-500/25 transition-all duration-300"
              >
                <Monitor className="w-5 h-5" />
                Launch Web App
              </a>
              <a 
                href={PLAY_STORE_URL} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-cyan-600 hover:via-blue-600 hover:to-indigo-700 transition-all duration-300 hover:scale-105 shadow-lg shadow-cyan-500/40"
              >
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="flex flex-col items-start">
                  <span className="text-[10px] leading-tight">GET IT ON</span>
                  <span className="text-lg font-bold leading-tight">Google Play</span>
                </div>
              </a>
              <Link to="/features" className="btn btn-large bg-white text-primary-500 border-2 border-white hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300">
                View All Features
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
